import { useEffect, useState } from "react";
import { appConfirm } from "@/components/appConfirm";
import { adminLogin, adminBookings, adminEvents, adminPurchasers, adminCreateEvent, adminGiftCards, adminGiftCardDetail, adminRedeemGiftCard, adminListDiscountCodes, adminCreateDiscountCode, adminToggleDiscountCode, adminSongRequests, adminWaitlist, adminReleaseTicket, adminCancelPreview, adminCancelSend, adminCocktailVoteResults, adminCocktailVoteSettings, adminCocktailVoteDelete, adminCocktailVoteExportUrl, extractApiError } from "@/lib/api";
import BookingsTracker from "@/components/BookingsTracker";
import { PortfolioAdmin } from "@/components/PortfolioAdmin";
import { toast } from "sonner";
import { X } from "lucide-react";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("ss_admin_token") || "");
  const [pw, setPw] = useState("");
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [newDiscount, setNewDiscount] = useState({ code: "", type: "percent", value: "", applies_to: "all", max_uses: "" });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [purchasers, setPurchasers] = useState(null);
  const [songRequests, setSongRequests] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [voteData, setVoteData] = useState(null);
  const [voteDeadline, setVoteDeadline] = useState("");
  const [redeemCard, setRedeemCard] = useState(null); // active card in redeem modal
  const [detailCard, setDetailCard] = useState(null); // full history detail card
  const [redeemForm, setRedeemForm] = useState({ amount_used: "", event_date: "", note: "" });
  const [redeeming, setRedeeming] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: "", date: "", time: "", location: "", description: "", image_url: "", price: "", total_tickets: "" });

  // Cancel Event modal state
  const [cancelEvent, setCancelEvent] = useState(null);           // event being cancelled
  const [cancelData, setCancelData] = useState(null);             // preview payload
  const [cancelForm, setCancelForm] = useState({ subject: "", body: "", issue_refunds: false });
  const [cancelResults, setCancelResults] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSending, setCancelSending] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const openCancelModal = async (ev) => {
    setCancelEvent(ev);
    setCancelData(null);
    setCancelResults(null);
    setCancelLoading(true);
    try {
      const data = await adminCancelPreview(token, ev.id);
      setCancelData(data);
      setCancelForm({ subject: data.default_subject, body: data.default_body, issue_refunds: false });
    } catch (err) {
      toast.error(extractApiError(err, "Could not load cancellation preview"));
      setCancelEvent(null);
    } finally {
      setCancelLoading(false);
    }
  };

  const closeCancelModal = () => {
    setCancelEvent(null); setCancelData(null); setCancelResults(null); setCancelConfirmOpen(false);
  };

  const sendCancellations = async () => {
    if (!cancelEvent) return;
    setCancelSending(true);
    try {
      const res = await adminCancelSend(token, cancelEvent.id, {
        subject: cancelForm.subject.trim(),
        body: cancelForm.body.trim(),
        issue_refunds: !!cancelForm.issue_refunds,
      });
      setCancelResults(res);
      setCancelConfirmOpen(false);
      const em = res.emails; const rf = res.refunds;
      toast.success(`Sent ${em.sent} · Skipped ${em.skipped} · Refunded ${rf.refunded}`);
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Cancellation failed"));
    } finally {
      setCancelSending(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await adminLogin(pw);
      localStorage.setItem("ss_admin_token", r.token);
      setToken(r.token);
      toast.success("Signed in");
    } catch { toast.error("Invalid password"); }
  };

  const load = async () => {
    try {
      const [b, e, g, d] = await Promise.all([adminBookings(token), adminEvents(token), adminGiftCards(token), adminListDiscountCodes(token)]);
      setBookings(b); setEvents(e); setGiftCards(g); setDiscountCodes(d);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem("ss_admin_token"); setToken(""); }
    }
  };
  useEffect(() => { if (token) load(); }, [token]);
  useEffect(() => { if (token && tab === "cocktail vote") loadVotes(); }, [token, tab]);

  const viewPurchasers = async (ev) => {
    setSelectedEvent(ev);
    setSongRequests([]);
    try {
      const [p, s] = await Promise.all([adminPurchasers(token, ev.id), adminSongRequests(token, ev.id)]);
      setPurchasers(p);
      setSongRequests(s);
    } catch { toast.error("Could not load event detail"); }
  };

  const openRedeem = (card) => {
    setRedeemCard(card);
    setRedeemForm({ amount_used: String(Math.floor(card.balance)), event_date: "", note: "" });
  };

  const openRedeemFromBooking = async (booking) => {
    try {
      const card = await adminGiftCardDetail(token, booking.gift_card_code);
      const preAmount = Math.min(
        Math.floor(booking.gift_credit_applied || card.balance),
        Math.floor(card.balance)
      );
      setRedeemCard(card);
      setRedeemForm({
        amount_used: String(preAmount || Math.floor(card.balance)),
        event_date: booking.event_date || "",
        note: `Booking: ${booking.name} · ${booking.event_type} (${booking.id.slice(0, 8)})`,
      });
    } catch (err) {
      toast.error(extractApiError(err, "Could not load gift card"));
    }
  };
  const submitRedeem = async (e) => {
    e.preventDefault();
    if (!redeemCard) return;
    setRedeeming(true);
    try {
      const updated = await adminRedeemGiftCard(token, redeemCard.code, {
        amount_used: Number(redeemForm.amount_used) || 0,
        event_date: redeemForm.event_date,
        note: redeemForm.note,
      });
      toast.success(`Redeemed $${redeemForm.amount_used}. Remaining $${Math.floor(updated.balance)}.`);
      setRedeemCard(null);
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Could not redeem"));
    } finally { setRedeeming(false); }
  };

  const viewHistory = async (code) => {
    try {
      const full = await adminGiftCardDetail(token, code);
      setDetailCard(full);
    } catch { toast.error("Could not load history"); }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      await adminCreateEvent(token, { ...newEvent, price: Number(newEvent.price), total_tickets: Number(newEvent.total_tickets) });
      toast.success("Event created");
      setNewEvent({ name: "", date: "", time: "", location: "", description: "", image_url: "", price: "", total_tickets: "" });
      load();
    } catch { toast.error("Could not create event"); }
  };

  const loadVotes = async () => {
    try {
      const data = await adminCocktailVoteResults(token);
      setVoteData(data);
      setVoteDeadline(data?.settings?.deadline_iso ? data.settings.deadline_iso.slice(0, 16) : "");
    } catch (err) { toast.error(extractApiError(err, "Could not load votes")); }
  };

  const saveVoteSettings = async (partial) => {
    try {
      await adminCocktailVoteSettings(token, partial);
      await loadVotes();
      toast.success("Voting settings saved");
    } catch (err) { toast.error(extractApiError(err, "Could not save settings")); }
  };

  const deleteVote = async (voteId) => {
    if (!(await appConfirm("Remove this vote? This cannot be undone."))) return;
    try {
      await adminCocktailVoteDelete(token, voteId);
      await loadVotes();
      toast.success("Vote removed");
    } catch (err) { toast.error(extractApiError(err, "Could not remove vote")); }
  };

  const createDiscount = async (e) => {
    e.preventDefault();
    try {
      await adminCreateDiscountCode(token, {
        code: newDiscount.code.trim().toUpperCase(),
        type: newDiscount.type,
        value: Number(newDiscount.value),
        applies_to: newDiscount.applies_to,
        max_uses: newDiscount.max_uses ? Number(newDiscount.max_uses) : null,
      });
      toast.success("Discount code created");
      setNewDiscount({ code: "", type: "percent", value: "", applies_to: "all", max_uses: "" });
      load();
    } catch (err) { toast.error(extractApiError(err, "Could not create")); }
  };
  const toggleDiscount = async (code, active) => {
    try {
      await adminToggleDiscountCode(token, code, active);
      toast.success(active ? "Activated" : "Deactivated");
      load();
    } catch { toast.error("Could not toggle"); }
  };

  if (!token) {
    return (
      <div className="pt-40 pb-24 mx-auto max-w-md px-5" data-testid="admin-login">
        <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Admin</div>
        <h1 className="mt-4 font-display text-5xl">Sign in.</h1>
        <form onSubmit={login} className="mt-8 space-y-5">
          <input type="password" data-testid="admin-password" className="editorial-input" placeholder="Password"
                 value={pw} onChange={(e) => setPw(e.target.value)} required />
          <button type="submit" data-testid="admin-login-btn" className="btn-primary">Sign In</button>
        </form>
      </div>
    );
  }

  return (
    <div className="pt-40 pb-24 mx-auto max-w-[1400px] px-5 md:px-10" data-testid="admin-panel">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-5xl">Admin</h1>
        <button data-testid="admin-logout-btn" onClick={() => { localStorage.removeItem("ss_admin_token"); setToken(""); }} className="btn-outline">Sign out</button>
      </div>

      <div data-testid="admin-tab-row" className="flex gap-3 border-b overflow-x-auto whitespace-nowrap -mx-5 md:mx-0 px-5 md:px-0 no-scrollbar" style={{ borderColor: "var(--ss-border)" }}>
        {["bookings", "events", "cocktail vote", "gift cards", "discounts", "portfolio", "create"].map(t => (
          <button key={t} data-testid={`admin-tab-${t.replace(/ /g,'-')}`} onClick={() => setTab(t)}
                  className="font-label text-xs px-4 py-3"
                  style={{ color: tab === t ? "var(--ss-rust)" : "var(--ss-cream-dim)", borderBottom: tab === t ? "1px solid var(--ss-rust)" : "1px solid transparent" }}>
            {t}
          </button>
        ))}
      </div>

      {tab === "gift cards" && (
        <div className="mt-8 space-y-3" data-testid="admin-gift-cards">
          {giftCards.length === 0 && <p style={{ color: "var(--ss-cream-dim)" }}>No gift cards issued yet.</p>}
          {giftCards.map(g => (
            <div key={g.id} className="p-6 border flex flex-wrap items-center justify-between gap-4" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }}
                 data-testid={`gift-card-row-${g.code}`}>
              <div>
                <div className="font-display text-2xl tracking-widest" style={{ color: "var(--ss-rust)" }}>{g.code}</div>
                <div className="text-sm mt-1" style={{ color: "var(--ss-cream-dim)" }}>
                  Purchased ${Math.floor(g.amount)} • Balance <span style={{ color: "var(--ss-cream)" }}>${Math.floor(g.balance)}</span> • {g.buyer_name} ({g.buyer_email})
                  {g.recipient_name && ` → ${g.recipient_name}`}
                </div>
                <div className="font-label text-[0.65rem] mt-1" style={{ color: "var(--ss-muted)" }}>
                  Issued {new Date(g.created_at).toLocaleString()} • {(g.redemptions || []).length} redemption(s)
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="font-label text-xs" style={{ color: g.redeemed ? "var(--ss-muted)" : "var(--ss-gold)" }}>
                  {g.redeemed ? "Redeemed" : "Active"}
                </div>
                {(g.redemptions || []).length > 0 && (
                  <button data-testid={`gift-history-${g.code}`} onClick={() => viewHistory(g.code)}
                          className="font-label text-[0.65rem] px-3 py-2 border"
                          style={{ borderColor: "var(--ss-border)", color: "var(--ss-cream-dim)" }}>
                    History
                  </button>
                )}
                {!g.redeemed && (
                  <button data-testid={`gift-redeem-${g.code}`} onClick={() => openRedeem(g)} className="btn-primary">Redeem</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "bookings" && (
        <BookingsTracker
          bookings={bookings}
          token={token}
          giftCards={giftCards}
          onReload={load}
          onOpenGiftRedeem={openRedeemFromBooking}
        />
      )}

      {tab === "events" && (
        <div className="mt-8 grid gap-4" data-testid="admin-events">
          {events.map(e => (
            <div key={e.id} className="p-6 border flex flex-wrap items-center justify-between gap-4" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }}>
              <div>
                <div className="font-display text-2xl">{e.name}</div>
                <div className="text-sm mt-1" style={{ color: "var(--ss-cream-dim)" }}>{e.date} • {e.time} • {e.location}</div>
                <div className="text-sm" style={{ color: "var(--ss-cream-dim)" }}>${e.price} • Sold {e.tickets_sold || 0} / {e.total_tickets}</div>
              </div>
              <button data-testid={`view-purchasers-${e.id}`} onClick={() => viewPurchasers(e)} className="btn-outline">View Purchasers</button>
              <button
                data-testid={`cancel-event-btn-${e.id}`}
                onClick={() => openCancelModal(e)}
                className="btn-primary"
                style={{ background: e.cancelled ? "#4a463f" : "var(--ss-rust)" }}
                title={e.cancelled ? "Event already cancelled — re-open to email late buyers" : "Cancel event"}
              >
                {e.cancelled ? "Cancelled ✓" : "Cancel Event"}
              </button>
            </div>
          ))}
          {selectedEvent && purchasers && (
            <div className="mt-6 p-6 border" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }} data-testid="purchasers-panel">
              <div className="flex justify-between items-center mb-4">
                <div className="font-display text-2xl">{selectedEvent.name} — Purchasers</div>
                <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>Total tickets sold: {purchasers.total_tickets_sold}</div>
              </div>
              {purchasers.purchasers.length === 0 && <p style={{ color: "var(--ss-cream-dim)" }}>No purchases yet.</p>}
              <div className="space-y-2">
                {purchasers.purchasers.map((p, i) => (
                  <div key={i} className="text-sm border-b py-2" style={{ borderColor: "var(--ss-border)" }}>
                    <span className="font-body">{p.customer_name}</span> — {p.customer_email} • {p.customer_phone} • {p.quantity} tix • ${p.amount}
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--ss-border)" }} data-testid="admin-song-requests">
                <div className="flex justify-between items-center mb-4">
                  <div className="font-display text-2xl">Song Requests</div>
                  <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>{songRequests.length} total</div>
                </div>
                {songRequests.length === 0 && <p style={{ color: "var(--ss-cream-dim)" }}>No song requests yet.</p>}
                <div className="space-y-2">
                  {songRequests.map((s) => (
                    <div key={s.id} className="text-sm border-b py-2 flex items-center gap-4" style={{ borderColor: "var(--ss-border)" }} data-testid={`song-row-${s.id}`}>
                      <div className="font-label text-xs px-2 py-1 border" style={{ borderColor: "var(--ss-rust)", color: "var(--ss-rust)", minWidth: 42, textAlign: "center" }}>
                        {s.votes || 0} ↑
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-body" style={{ color: "var(--ss-cream)" }}>{s.song}</span>
                        {s.artist && <span style={{ color: "var(--ss-cream-dim)" }}> — {s.artist}</span>}
                        <span style={{ color: "var(--ss-muted)" }}> • {s.requester_name || "anonymous"}</span>
                        {s.dedication && <div className="mt-1 italic" style={{ color: "var(--ss-cream-dim)" }}>“{s.dedication}”</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--ss-border)" }} data-testid="admin-waitlist">
                <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                  <div className="font-display text-2xl">Waitlist</div>
                  <div className="flex items-center gap-3">
                    <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>{waitlist.length} waiting</div>
                    <button
                      data-testid="release-ticket-btn"
                      onClick={async () => {
                        if (!selectedEvent) return;
                        const raw = window.prompt("How many tickets to release?", "1");
                        if (!raw) return;
                        const count = Math.max(1, parseInt(raw, 10) || 1);
                        try {
                          const res = await adminReleaseTicket(token, selectedEvent.id, { count, origin_url: window.location.origin });
                          toast.success(`Released ${res.released}. Notified ${res.notified.length} waitlister(s).`);
                          viewPurchasers(selectedEvent);
                          load();
                        } catch (err) {
                          toast.error(extractApiError(err, "Release failed"));
                        }
                      }}
                      className="btn-primary"
                      disabled={(selectedEvent?.tickets_sold || 0) < 1}
                    >
                      Release Ticket
                    </button>
                  </div>
                </div>
                {waitlist.length === 0 && <p style={{ color: "var(--ss-cream-dim)" }}>No one on the waitlist yet.</p>}
                <div className="space-y-2">
                  {waitlist.map((w) => (
                    <div key={w.id} className="text-sm border-b py-2 flex items-center gap-3 flex-wrap" style={{ borderColor: "var(--ss-border)" }} data-testid={`waitlist-row-${w.id}`}>
                      <span className="font-body" style={{ color: "var(--ss-cream)" }}>{w.name}</span>
                      <span style={{ color: "var(--ss-cream-dim)" }}>— {w.email}</span>
                      {w.phone && <span style={{ color: "var(--ss-muted)" }}>• {w.phone}</span>}
                      <span style={{ color: "var(--ss-muted)" }}>• {new Date(w.created_at).toLocaleString()}</span>
                      {w.notified_at && (
                        <span className="font-label text-[0.6rem] px-2 py-1 border ml-auto" style={{ borderColor: "var(--ss-gold)", color: "var(--ss-gold)" }}>
                          NOTIFIED {new Date(w.notified_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "cocktail vote" && (
        <div className="mt-8 space-y-8" data-testid="admin-cocktail-vote">
          {!voteData && <p style={{ color: "var(--ss-cream-dim)" }}>Loading vote data…</p>}
          {voteData && (
            <>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-5 border" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }}>
                  <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-gold)" }}>TOTAL VOTES</div>
                  <div className="mt-1 font-display text-5xl" data-testid="vote-total">{voteData.total_voters}</div>
                </div>
                <div className="p-5 border md:col-span-2" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-rust)" }}>VOTING</div>
                    <span className="font-body text-sm">{voteData.settings.voting_open ? "Open" : "Closed"}</span>
                    <button data-testid="vote-toggle-btn" onClick={() => saveVoteSettings({ open: !voteData.settings.open })}
                            className="ml-auto font-label text-[0.65rem] px-3 py-1 border"
                            style={{ borderColor: "var(--ss-rust)", color: "var(--ss-rust)" }}>
                      {voteData.settings.open ? "CLOSE VOTING" : "OPEN VOTING"}
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <div className="flex flex-col">
                      <label className="font-label text-[0.6rem] mb-1" style={{ color: "var(--ss-cream-dim)" }}>Deadline (optional)</label>
                      <input type="datetime-local" data-testid="vote-deadline" className="editorial-input"
                             value={voteDeadline} onChange={(e) => setVoteDeadline(e.target.value)} />
                    </div>
                    <button data-testid="vote-deadline-save" className="btn-primary"
                            onClick={() => saveVoteSettings({ deadline_iso: voteDeadline ? new Date(voteDeadline).toISOString() : "" })}>
                      SAVE DEADLINE
                    </button>
                    <a data-testid="vote-export-csv" href={`${adminCocktailVoteExportUrl()}?_p=${encodeURIComponent(token)}`}
                       onClick={(e) => { e.preventDefault(); fetch(adminCocktailVoteExportUrl(), { headers: { "X-Admin-Password": token } })
                         .then(r => r.blob()).then(b => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'cocktail-votes.csv'; a.click(); }); }}
                       className="font-label text-[0.65rem] px-3 py-2 border ml-auto"
                       style={{ borderColor: "var(--ss-gold)", color: "var(--ss-gold)" }}>
                      EXPORT CSV
                    </a>
                  </div>
                </div>
              </div>

              <div className="border" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }}>
                <div className="p-5 border-b flex justify-between" style={{ borderColor: "var(--ss-border)" }}>
                  <div className="font-display text-2xl">Ranking</div>
                  <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>MOST → LEAST POPULAR</div>
                </div>
                <div className="divide-y" style={{ borderColor: "var(--ss-border)" }}>
                  {voteData.ranking.map(r => (
                    <div key={r.key} className="p-4 flex items-center gap-4" data-testid={`vote-rank-${r.key}`} style={{ borderColor: "var(--ss-border)" }}>
                      <div className="font-display text-2xl" style={{ color: "var(--ss-rust)", minWidth: 40 }}>#{r.rank}</div>
                      <div className="flex-1">
                        <div className="font-display text-xl">{r.name}</div>
                        <div className="h-1 mt-2 rounded" style={{ background: "var(--ss-border)" }}>
                          <div style={{ width: `${r.percent}%`, height: "100%", background: "var(--ss-rust)" }} />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-2xl">{r.votes}</div>
                        <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-cream-dim)" }}>{r.percent}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }}>
                <div className="p-5 border-b flex justify-between" style={{ borderColor: "var(--ss-border)" }}>
                  <div className="font-display text-2xl">Submissions</div>
                  <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>{voteData.submissions.length} total</div>
                </div>
                {voteData.submissions.length === 0 && <p className="p-5" style={{ color: "var(--ss-cream-dim)" }}>No votes yet.</p>}
                <div className="divide-y" style={{ borderColor: "var(--ss-border)" }}>
                  {voteData.submissions.map(s => (
                    <div key={s.id} className="p-4 text-sm flex flex-wrap gap-3 items-center" data-testid={`vote-sub-${s.id}`}>
                      <div className="flex-1 min-w-0">
                        <div><strong>{s.customer_name || "—"}</strong> · {s.customer_email}</div>
                        <div className="mt-1" style={{ color: "var(--ss-cream-dim)" }}>
                          {(s.cocktails || []).map(k => (voteData.ranking.find(r => r.key === k)?.name) || k).join(", ")}
                        </div>
                        <div className="mt-1 font-label text-[0.6rem]" style={{ color: "var(--ss-muted)" }}>{new Date(s.created_at).toLocaleString()}</div>
                      </div>
                      <button data-testid={`vote-delete-${s.id}`} onClick={() => deleteVote(s.id)}
                              className="font-label text-[0.6rem] px-3 py-1 border"
                              style={{ borderColor: "var(--ss-rust)", color: "var(--ss-rust)" }}>
                        REMOVE
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "discounts" && (
        <div className="mt-8 grid gap-8" data-testid="admin-discounts">
          <form onSubmit={createDiscount} className="p-6 border grid sm:grid-cols-2 gap-5" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }} data-testid="admin-discount-form">
            <div className="sm:col-span-2 font-label text-xs" style={{ color: "var(--ss-rust)" }}>Create Discount Code</div>
            <input required data-testid="new-discount-code" className="editorial-input" placeholder="Code (e.g. LAUNCH10)"
                   value={newDiscount.code} onChange={(e) => setNewDiscount({ ...newDiscount, code: e.target.value.toUpperCase() })} />
            <select data-testid="new-discount-type" className="editorial-input"
                    value={newDiscount.type} onChange={(e) => setNewDiscount({ ...newDiscount, type: e.target.value })}>
              <option value="percent">Percent (%)</option>
              <option value="fixed">Fixed ($)</option>
            </select>
            <input required type="number" step="1" min="1" data-testid="new-discount-value" className="editorial-input" placeholder={newDiscount.type === "percent" ? "e.g. 10 (percent)" : "e.g. 50 (dollars)"}
                   value={newDiscount.value} onChange={(e) => setNewDiscount({ ...newDiscount, value: e.target.value })} />
            <select data-testid="new-discount-applies" className="editorial-input"
                    value={newDiscount.applies_to} onChange={(e) => setNewDiscount({ ...newDiscount, applies_to: e.target.value })}>
              <option value="all">All (bundle + events)</option>
              <option value="bundle">Bundle only</option>
              <option value="events">Events only</option>
            </select>
            <input type="number" min="1" data-testid="new-discount-max-uses" className="editorial-input sm:col-span-2" placeholder="Max uses (optional, blank = unlimited)"
                   value={newDiscount.max_uses} onChange={(e) => setNewDiscount({ ...newDiscount, max_uses: e.target.value })} />
            <div className="sm:col-span-2">
              <button type="submit" data-testid="new-discount-submit" className="btn-primary">Create Code</button>
            </div>
          </form>

          <div className="grid gap-3">
            {discountCodes.length === 0 && <p style={{ color: "var(--ss-cream-dim)" }}>No discount codes yet.</p>}
            {discountCodes.map(d => (
              <div key={d.id} className="p-6 border flex flex-wrap items-center justify-between gap-4" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }} data-testid={`discount-row-${d.code}`}>
                <div>
                  <div className="font-display text-2xl tracking-widest" style={{ color: "var(--ss-rust)" }}>{d.code}</div>
                  <div className="text-sm mt-1" style={{ color: "var(--ss-cream-dim)" }}>
                    {d.type === "percent" ? `${d.value}% off` : `$${d.value} off`} · {d.applies_to} · {d.uses}/{d.max_uses ?? "∞"} uses
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="font-label text-xs" style={{ color: d.active ? "var(--ss-gold)" : "var(--ss-muted)" }}>
                    {d.active ? "Active" : "Inactive"}
                  </div>
                  <button data-testid={`discount-toggle-${d.code}`} onClick={() => toggleDiscount(d.code, !d.active)} className="btn-outline">
                    {d.active ? "Deactivate" : "Activate"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "portfolio" && <PortfolioAdmin token={token} />}

      {tab === "create" && (
        <form onSubmit={createEvent} className="mt-8 grid md:grid-cols-2 gap-6" data-testid="admin-create-event">
          {[
            ["name", "Event name"], ["date", "Date (YYYY-MM-DD)"], ["time", "Time (e.g. 7:00 PM)"],
            ["location", "Location"], ["image_url", "Image URL"], ["price", "Price"], ["total_tickets", "Total tickets"],
          ].map(([k, ph]) => (
            <input key={k} required className="editorial-input" placeholder={ph} data-testid={`new-event-${k}`}
                   value={newEvent[k]} onChange={(e) => setNewEvent({ ...newEvent, [k]: e.target.value })} />
          ))}
          <textarea required rows={4} className="editorial-input md:col-span-2" placeholder="Description" data-testid="new-event-description"
                    value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} />
          <div className="md:col-span-2">
            <button type="submit" data-testid="new-event-submit" className="btn-primary">Create Event</button>
          </div>
        </form>
      )}

      {/* Redeem modal */}
      {redeemCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: "rgba(0,0,0,0.8)" }} onClick={() => !redeeming && setRedeemCard(null)}
             data-testid="redeem-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitRedeem}
                className="w-full max-w-lg p-8 border" style={{ background: "var(--ss-bg-2)", borderColor: "var(--ss-border)" }}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Redeem</div>
                <h3 className="font-display text-3xl tracking-widest mt-1">{redeemCard.code}</h3>
                <div className="text-sm mt-1" style={{ color: "var(--ss-cream-dim)" }}>
                  Current balance <span style={{ color: "var(--ss-cream)" }}>${Math.floor(redeemCard.balance)}</span>
                </div>
              </div>
              <button type="button" onClick={() => setRedeemCard(null)} className="p-1 hover:text-[color:var(--ss-rust)]"><X size={20} /></button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>Amount to redeem ($)</label>
                <input type="number" min="0" max={Math.floor(redeemCard.balance)} required
                       className="editorial-input mt-1" data-testid="redeem-amount"
                       value={redeemForm.amount_used}
                       onChange={(e) => setRedeemForm({ ...redeemForm, amount_used: e.target.value })} />
              </div>
              <div>
                <label className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>Event date (optional)</label>
                <input type="date" className="editorial-input mt-1" data-testid="redeem-event-date"
                       value={redeemForm.event_date}
                       onChange={(e) => setRedeemForm({ ...redeemForm, event_date: e.target.value })} />
              </div>
              <div>
                <label className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>Note (booking ID, event name, etc.)</label>
                <textarea rows={3} className="editorial-input" data-testid="redeem-note"
                          value={redeemForm.note}
                          onChange={(e) => setRedeemForm({ ...redeemForm, note: e.target.value })} />
              </div>
              <div className="flex justify-between items-center pt-2">
                <div className="text-sm" style={{ color: "var(--ss-cream-dim)" }}>
                  Remaining after: <span style={{ color: "var(--ss-rust)" }}>${Math.max(0, Math.floor(redeemCard.balance) - (Number(redeemForm.amount_used) || 0))}</span>
                </div>
                <button type="submit" disabled={redeeming} data-testid="redeem-submit" className="btn-primary">
                  {redeeming ? "Saving…" : "Confirm Redemption"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* History modal */}
      {detailCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: "rgba(0,0,0,0.8)" }} onClick={() => setDetailCard(null)}
             data-testid="history-modal">
          <div onClick={(e) => e.stopPropagation()}
               className="w-full max-w-xl p-8 border max-h-[85vh] overflow-y-auto" style={{ background: "var(--ss-bg-2)", borderColor: "var(--ss-border)" }}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Redemption History</div>
                <h3 className="font-display text-3xl tracking-widest mt-1">{detailCard.code}</h3>
                <div className="text-sm mt-1" style={{ color: "var(--ss-cream-dim)" }}>
                  Purchased ${Math.floor(detailCard.amount)} • Balance ${Math.floor(detailCard.balance)}
                </div>
              </div>
              <button type="button" onClick={() => setDetailCard(null)} className="p-1"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              {(detailCard.redemptions || []).length === 0 && (
                <p style={{ color: "var(--ss-cream-dim)" }}>No redemptions yet.</p>
              )}
              {(detailCard.redemptions || []).slice().reverse().map((r, i) => (
                <div key={i} data-testid={`history-entry-${i}`} className="border-l-2 pl-4 py-1" style={{ borderColor: "var(--ss-rust)" }}>
                  <div className="flex justify-between">
                    <div className="font-display text-xl">−${Math.floor(r.amount_used)}</div>
                    <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-muted)" }}>{new Date(r.redeemed_at).toLocaleString()}</div>
                  </div>
                  <div className="text-sm mt-1" style={{ color: "var(--ss-cream-dim)" }}>
                    Remaining after: ${Math.floor(r.remaining_after)}
                    {r.event_date && ` • Event ${r.event_date}`}
                  </div>
                  {r.note && <div className="text-sm mt-1 italic" style={{ color: "var(--ss-cream-dim)" }}>{r.note}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Cancel Event modal */}
      {cancelEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: "rgba(0,0,0,0.85)" }}
             data-testid="cancel-event-modal">
          <div onClick={(e) => e.stopPropagation()}
               className="w-full max-w-3xl p-8 border max-h-[92vh] overflow-y-auto"
               style={{ background: "var(--ss-bg-2)", borderColor: "var(--ss-rust)" }}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Cancel Event</div>
                <h3 className="font-display text-3xl mt-1">{cancelEvent.name}</h3>
                <div className="text-sm mt-1" style={{ color: "var(--ss-cream-dim)" }}>
                  {cancelEvent.date} • {cancelEvent.time} • {cancelEvent.location}
                </div>
              </div>
              <button type="button" onClick={closeCancelModal} data-testid="cancel-modal-close" className="p-1"><X size={20} /></button>
            </div>

            {cancelLoading && <p style={{ color: "var(--ss-cream-dim)" }}>Loading purchaser list…</p>}

            {cancelData && !cancelResults && (
              <div className="space-y-6">
                <div className="grid sm:grid-cols-3 gap-4 text-sm">
                  <div className="p-3 border" style={{ borderColor: "var(--ss-border)" }} data-testid="cancel-stat-orders">
                    <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>Paid Orders</div>
                    <div className="font-display text-2xl mt-1">{cancelData.paid_orders}</div>
                  </div>
                  <div className="p-3 border" style={{ borderColor: "var(--ss-border)" }} data-testid="cancel-stat-recipients">
                    <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>Recipients</div>
                    <div className="font-display text-2xl mt-1">{cancelData.recipients.length}</div>
                  </div>
                  <div className="p-3 border" style={{ borderColor: "var(--ss-border)" }} data-testid="cancel-stat-refund">
                    <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>Refund Total</div>
                    <div className="font-display text-2xl mt-1">${Math.floor(cancelData.total_refund_pending)}</div>
                    {cancelData.total_refund_already > 0 && (
                      <div className="text-xs mt-1" style={{ color: "var(--ss-cream-dim)" }}>${Math.floor(cancelData.total_refund_already)} already refunded</div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="font-label text-xs mb-2" style={{ color: "var(--ss-rust)" }}>Recipients</div>
                  {cancelData.recipients.length === 0 ? (
                    <p style={{ color: "var(--ss-cream-dim)" }}>No paid buyers to notify.</p>
                  ) : (
                    <div className="border max-h-56 overflow-y-auto" style={{ borderColor: "var(--ss-border)" }}>
                      {cancelData.recipients.map((r, i) => (
                        <div key={i} data-testid={`cancel-recipient-${i}`}
                             className="text-sm px-3 py-2 flex justify-between items-center gap-3 border-b"
                             style={{ borderColor: "var(--ss-border)" }}>
                          <div className="min-w-0">
                            <span className="font-body">{r.name || "(no name)"}</span>
                            <span style={{ color: "var(--ss-cream-dim)" }}> — {r.email}</span>
                            {r.role === "bestie_partner" && <span className="ml-2 text-[0.65rem] font-label" style={{ color: "var(--ss-gold)" }}>BESTIE</span>}
                          </div>
                          <div className="flex items-center gap-2 text-[0.7rem] shrink-0">
                            {r.amount > 0 && <span style={{ color: "var(--ss-cream-dim)" }}>${Math.floor(r.amount)}</span>}
                            {r.already_emailed && <span className="font-label" style={{ color: "var(--ss-gold)" }}>ALREADY EMAILED</span>}
                            {r.refunded && <span className="font-label" style={{ color: "var(--ss-gold)" }}>REFUNDED</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>Subject</label>
                  <input type="text" data-testid="cancel-subject"
                         className="editorial-input mt-1"
                         value={cancelForm.subject}
                         onChange={(e) => setCancelForm({ ...cancelForm, subject: e.target.value })} />
                </div>

                <div>
                  <label className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>
                    Body (use <span style={{ color: "var(--ss-rust)" }}>[First Name]</span> — auto-replaced per recipient)
                  </label>
                  <textarea rows={9} data-testid="cancel-body"
                            className="editorial-input mt-1"
                            value={cancelForm.body}
                            onChange={(e) => setCancelForm({ ...cancelForm, body: e.target.value })} />
                </div>

                <label className="flex items-center gap-3 p-3 border cursor-pointer" style={{ borderColor: "var(--ss-rust)" }} data-testid="cancel-refund-toggle">
                  <input type="checkbox"
                         checked={cancelForm.issue_refunds}
                         onChange={(e) => setCancelForm({ ...cancelForm, issue_refunds: e.target.checked })} />
                  <div>
                    <div className="font-body">Also issue full Stripe refunds</div>
                    <div className="text-xs" style={{ color: "var(--ss-cream-dim)" }}>
                      Refunds ${Math.floor(cancelData.total_refund_pending)} across {cancelData.paid_orders} order(s) back to original card. Already-refunded orders are skipped.
                    </div>
                  </div>
                </label>

                <div className="flex justify-between items-center pt-2">
                  <button type="button" onClick={closeCancelModal} className="btn-outline" data-testid="cancel-modal-cancel-btn">Never mind</button>
                  <button type="button"
                          data-testid="cancel-send-btn"
                          disabled={!cancelForm.subject.trim() || !cancelForm.body.trim() || cancelData.recipients.length === 0}
                          onClick={() => setCancelConfirmOpen(true)}
                          className="btn-primary">
                    Send Cancellations →
                  </button>
                </div>
              </div>
            )}

            {cancelResults && (
              <div className="space-y-6" data-testid="cancel-results">
                <div className="grid sm:grid-cols-4 gap-3 text-sm">
                  <div className="p-3 border text-center" style={{ borderColor: "var(--ss-border)" }}>
                    <div className="font-display text-2xl">{cancelResults.emails.sent}</div>
                    <div className="font-label text-[0.6rem] mt-1" style={{ color: "var(--ss-cream-dim)" }}>SENT</div>
                  </div>
                  <div className="p-3 border text-center" style={{ borderColor: "var(--ss-border)" }}>
                    <div className="font-display text-2xl">{cancelResults.emails.skipped}</div>
                    <div className="font-label text-[0.6rem] mt-1" style={{ color: "var(--ss-cream-dim)" }}>SKIPPED (DUP)</div>
                  </div>
                  <div className="p-3 border text-center" style={{ borderColor: "var(--ss-border)" }}>
                    <div className="font-display text-2xl">{cancelResults.emails.failed}</div>
                    <div className="font-label text-[0.6rem] mt-1" style={{ color: "var(--ss-cream-dim)" }}>FAILED</div>
                  </div>
                  <div className="p-3 border text-center" style={{ borderColor: "var(--ss-border)" }}>
                    <div className="font-display text-2xl">{cancelResults.refunds.refunded}</div>
                    <div className="font-label text-[0.6rem] mt-1" style={{ color: "var(--ss-cream-dim)" }}>REFUNDED</div>
                  </div>
                </div>

                <div>
                  <div className="font-label text-xs mb-2" style={{ color: "var(--ss-rust)" }}>Email Results</div>
                  <div className="border max-h-56 overflow-y-auto" style={{ borderColor: "var(--ss-border)" }}>
                    {cancelResults.emails.results.map((r, i) => (
                      <div key={i} className="text-sm px-3 py-2 flex justify-between border-b" style={{ borderColor: "var(--ss-border)" }}>
                        <span>{r.name || r.email} — {r.email}</span>
                        <span className="font-label text-[0.65rem]" style={{ color: r.status === "SENT" ? "var(--ss-gold)" : r.status === "FAILED" ? "var(--ss-rust)" : "var(--ss-cream-dim)" }}>{r.status}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {cancelResults.refunds.results.length > 0 && (
                  <div>
                    <div className="font-label text-xs mb-2" style={{ color: "var(--ss-rust)" }}>Refund Results</div>
                    <div className="border max-h-56 overflow-y-auto" style={{ borderColor: "var(--ss-border)" }}>
                      {cancelResults.refunds.results.map((r, i) => (
                        <div key={i} className="text-sm px-3 py-2 flex justify-between border-b" style={{ borderColor: "var(--ss-border)" }}>
                          <span>${Math.floor(r.amount || 0)} — {r.email}</span>
                          <span className="font-label text-[0.65rem]" style={{ color: r.status === "REFUNDED" ? "var(--ss-gold)" : r.status === "FAILED" ? "var(--ss-rust)" : "var(--ss-cream-dim)" }}>{r.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button type="button" onClick={closeCancelModal} className="btn-primary" data-testid="cancel-results-close">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cancel confirm overlay */}
      {cancelConfirmOpen && cancelData && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
             style={{ background: "rgba(0,0,0,0.9)" }}
             data-testid="cancel-confirm-overlay">
          <div className="w-full max-w-md p-6 border" style={{ background: "var(--ss-bg-2)", borderColor: "var(--ss-rust)" }}>
            <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Confirm Cancellation</div>
            <h4 className="font-display text-2xl mt-1">Are you sure?</h4>
            <p className="text-sm mt-3" style={{ color: "var(--ss-cream-dim)" }}>
              This will email <strong style={{ color: "var(--ss-cream)" }}>{cancelData.recipients.filter(r => !r.already_emailed).length}</strong> new recipient(s)
              {cancelForm.issue_refunds ? <> and refund <strong style={{ color: "var(--ss-cream)" }}>${Math.floor(cancelData.total_refund_pending)}</strong> via Stripe</> : null}.
              Duplicates are auto-skipped.
            </p>
            <div className="flex justify-between mt-6">
              <button type="button" onClick={() => setCancelConfirmOpen(false)} disabled={cancelSending} className="btn-outline" data-testid="cancel-confirm-no">Go back</button>
              <button type="button" onClick={sendCancellations} disabled={cancelSending} className="btn-primary" data-testid="cancel-confirm-yes">
                {cancelSending ? "Sending…" : "Yes, send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
